import React, { useEffect, useState } from "react";
import api, { fmtInt, fmtPct, API } from "@/api";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { useCountry } from "@/country";
import { useAuth, can } from "@/auth";
import { PageHeader, KPI, Tile, Loading } from "@/components/Bits";
import { ForecastVsActualTile } from "@/components/Outcomes";
import { ChatCircleDots, ClockCounterClockwise, Target, ClipboardText, FilePdf } from "@phosphor-icons/react";
import { useShowPrices } from "@/showPrices";
import DrilldownModal from "@/components/DrilldownModal";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  LineChart,
  Line,
} from "recharts";

const STATUS_COLORS = {
  Hot: "#ED2027",
  Warm: "#FF9500",
  Cold: "#007AFF",
  "Past/Now": "#86868B",
  Unknown: "#D2D2D7",
};

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { fmt, currency } = useCurrency();
  const { code: country } = useCountry();
  const { user } = useAuth();
  const { showPrices } = useShowPrices();
  const [d, setD] = useState(null);
  const [activity, setActivity] = useState(null);
  const [drill, setDrill] = useState(null); // "operators" | "cities" | "platforms"

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    api.get("/activity/recent?limit=10").then((r) => setActivity(r.data.rows));
  }, []);

  const downloadReport = () => {
    const url = `${API}/reports/monthly.pdf?country=${country}&lang=${lang}`;
    window.open(url, "_blank");
  };

  if (!d) return <Loading />;

  // No data for this country yet — show a friendly empty state
  if ((d.kpis?.total_units || 0) === 0) {
    return (
      <div data-testid="dashboard-page">
        <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.subtitle")} />
        <div className="p-4 md:p-10">
          <Tile testId="dashboard-empty">
            <div className="text-center py-16">
              <div className="text-[44px] mb-4">🗺️</div>
              <h2 className="text-[20px] font-semibold tracking-tight mb-2" style={{ letterSpacing: "-0.025em" }}>
                {t("country.empty.title")}
              </h2>
              <p className="text-[14px] max-w-md mx-auto" style={{ color: "var(--text-secondary)" }}>
                {t("country.empty.hint")}
              </p>
            </div>
          </Tile>
        </div>
      </div>
    );
  }

  const statusData = Object.entries(d.by_status).map(([name, count]) => ({ name, count }));
  const axisStyle = { fontFamily: "JetBrains Mono", fontSize: 11, fill: "#86868b" };
  const tipStyle = { background: "#fff", border: "1px solid #d2d2d7", borderRadius: 10, fontSize: 12, padding: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.06)" };

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={t("dashboard.title")}
        subtitle={t("dashboard.subtitle")}
        right={
          can(user, "admin") ? (
            <button onClick={downloadReport} className="btn-accent flex items-center gap-2" data-testid="dashboard-monthly-pdf">
              <FilePdf size={14} weight="bold" />
              {lang === "fr" ? "Rapport mensuel" : "Monthly report"}
            </button>
          ) : null
        }
      />
      <div className="p-4 md:p-10 space-y-6 md:space-y-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 stagger">
          <KPI testId="kpi-total" label={t("kpi.total_units")} value={fmtInt(d.kpis.total_units)} sub="installed base units" />
          <KPI testId="kpi-wabtec" label={t("kpi.wabtec_units")} value={fmtInt(d.kpis.wabtec_units)} accent sub={`${fmtPct(d.kpis.wabtec_share_pct)} of total`} />
          <KPI testId="kpi-addressable" label={t("kpi.addressable")} value={fmtInt(d.kpis.addressable_units)} sub="units to win" />
          <KPI testId="kpi-share" label={t("kpi.share")} value={fmtPct(d.kpis.wabtec_share_pct)} accent sub="of total installed base" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 stagger">
          <KPI testId="kpi-platforms" label={t("kpi.platforms")} value={fmtInt(d.kpis.platforms)}
               sub={lang === "fr" ? "cliquer pour le détail →" : "click for details →"}
               onClick={() => setDrill("platforms")} />
          <KPI testId="kpi-operators" label={t("kpi.operators")} value={fmtInt(d.kpis.operators)}
               sub={lang === "fr" ? "cliquer pour le détail →" : "click for details →"}
               onClick={() => setDrill("operators")} />
          <KPI testId="kpi-cities" label={t("kpi.cities")} value={fmtInt(d.kpis.cities)}
               sub={lang === "fr" ? "cliquer pour le détail →" : "click for details →"}
               onClick={() => setDrill("cities")} />
          <KPI testId="kpi-trainsets" label={t("kpi.trainsets")} value={fmtInt(d.kpis.trainsets)} />
        </div>

        {/* Pipeline KPIs */}
        {showPrices && (
          <div>
            <div className="label-tech mb-3">Pipeline · {currency}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 stagger">
              <KPI testId="pipe-m1" label={t("pipeline.m1")} value={fmt(d.pipeline.m1_theoretical)} />
              <KPI testId="pipe-m2" label={t("pipeline.m2")} value={fmt(d.pipeline.m2_addressable)} />
              <KPI testId="pipe-m3" label={t("pipeline.m3")} value={fmt(d.pipeline.m3_weighted)} accent />
              <KPI testId="pipe-active" label={t("pipeline.active")} value={fmt(d.pipeline.active)} accent />
            </div>
          </div>
        )}

        {/* By status + by year */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 stagger">
          <Tile testId="chart-by-status">
            <div className="label-tech mb-5">{t("pipeline.byStatus")}</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={statusData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#e8e8ec" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={axisStyle} />
                <YAxis axisLine={false} tickLine={false} tick={axisStyle} />
                <Tooltip contentStyle={tipStyle} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {statusData.map((s) => (
                    <Cell key={s.name} fill={STATUS_COLORS[s.name] || "#888"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Tile>

          <Tile testId="chart-by-year">
            <div className="label-tech mb-5">{showPrices ? t("pipeline.byYear") : (lang === "fr" ? "Opportunités par année OVH" : "Opportunities by OVH year")}</div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={d.by_year} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#e8e8ec" vertical={false} />
                <XAxis dataKey="year" axisLine={false} tickLine={false} tick={axisStyle} />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={axisStyle}
                  tickFormatter={(v) => showPrices ? fmt(v).replace(/\s.*$/, "") : String(v)}
                />
                <Tooltip contentStyle={tipStyle} formatter={(v) => showPrices ? fmt(v) : `${v} ${lang === "fr" ? "opp." : "opps"}`} />
                <Line type="monotone" dataKey={showPrices ? "value" : "count"} stroke="#ED2027" strokeWidth={2.5} dot={{ fill: "#ED2027", r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </Tile>
        </div>

        {/* Top operators / sub-systems */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 stagger">
          <Tile testId="chart-by-operator">
            <div className="label-tech mb-5">{showPrices ? t("pipeline.byOperator") : (lang === "fr" ? "Équipements par opérateur" : "Equipment by operator")}</div>
            <ResponsiveContainer width="100%" height={Math.max(260, d.by_operator.length * 30)}>
              <BarChart data={d.by_operator} layout="vertical" margin={{ top: 4, right: 24, left: 120, bottom: 4 }}>
                <CartesianGrid stroke="#e8e8ec" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={axisStyle} tickFormatter={(v) => showPrices ? fmt(v).replace(/\s.*$/, "") : String(v)} />
                <YAxis type="category" dataKey="operator" axisLine={false} tickLine={false} tick={{ ...axisStyle, fontFamily: "Inter", fill: "#1d1d1f" }} width={118} />
                <Tooltip contentStyle={tipStyle} formatter={(v) => showPrices ? fmt(v) : `${v} ${lang === "fr" ? "unités" : "units"}`} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Bar dataKey={showPrices ? "value" : "qty"} fill="#ED2027" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Tile>

          <Tile testId="chart-by-sub">
            <div className="label-tech mb-5">{showPrices ? t("pipeline.bySub") : (lang === "fr" ? "Équipements par sous-système" : "Equipment by sub-system")}</div>
            <ResponsiveContainer width="100%" height={Math.max(260, d.by_sub_system.length * 30)}>
              <BarChart data={d.by_sub_system} layout="vertical" margin={{ top: 4, right: 24, left: 120, bottom: 4 }}>
                <CartesianGrid stroke="#e8e8ec" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={axisStyle} tickFormatter={(v) => showPrices ? fmt(v).replace(/\s.*$/, "") : String(v)} />
                <YAxis type="category" dataKey="sub_system" axisLine={false} tickLine={false} tick={{ ...axisStyle, fontFamily: "Inter", fill: "#1d1d1f" }} width={118} />
                <Tooltip contentStyle={tipStyle} formatter={(v) => showPrices ? fmt(v) : `${v} ${lang === "fr" ? "unités" : "units"}`} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Bar dataKey={showPrices ? "value" : "qty"} fill="#1d1d1f" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Tile>
        </div>

        {/* Forecast vs actual + Recent activity, side by side on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ForecastVsActualTile />
          <ActivityWidget rows={activity} t={t} />
        </div>
      </div>
      {drill && <DrilldownModal kind={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

function ActivityWidget({ rows, t }) {
  return (
    <Tile testId="dashboard-activity">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={15} weight="fill" style={{ color: "var(--accent)" }} />
          <div className="label-tech" style={{ fontSize: 11 }}>{t("activity.title")}</div>
        </div>
        <a href="/account-plans" className="text-[11.5px] font-mono hover:text-[var(--accent)] transition-colors"
           style={{ color: "var(--text-tertiary)" }} data-testid="activity-link-plans">
          {t("activity.viewAll")} →
        </a>
      </div>
      {!rows ? (
        <div className="text-[12px] font-mono" style={{ color: "var(--text-tertiary)" }}>{t("common.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="text-[13px] py-8 text-center" style={{ color: "var(--text-tertiary)" }}>{t("activity.empty")}</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => <ActivityRow key={r.id} row={r} t={t} last={i === rows.length - 1} />)}
        </div>
      )}
    </Tile>
  );
}

function ActivityRow({ row, t, last }) {
  const isComment = row.kind === "comment";
  const TypeIcon = row.entity_type === "account_plan" ? ClipboardText : Target;
  const KIND_TONE = {
    comment: { color: "var(--text-primary)", label: t("comments.kind.comment") },
    create:  { color: "var(--success)", label: t("comments.kind.create") },
    update:  { color: "var(--info)", label: t("comments.kind.update") },
    delete:  { color: "var(--accent)", label: t("comments.kind.delete") },
  };
  const tone = KIND_TONE[row.kind] || KIND_TONE.comment;
  const initials = (row.user_name || row.user_email || "?").trim().slice(0, 2).toUpperCase();
  const when = new Date(row.created_at);
  const ago = relTime(when, t);
  const href = row.entity_type === "account_plan" ? "/account-plans" : "/opportunities";

  return (
    <a href={href}
       className="flex items-start gap-3 px-3 py-3 rounded-[10px] hover:bg-[var(--bg-subtle)] transition-colors no-underline"
       style={{ borderBottom: last ? "none" : "1px solid var(--border)", color: "inherit" }}
       data-testid={`activity-row-${row.id}`}>
      {isComment ? (
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10.5px] font-semibold text-white shrink-0"
             style={{ background: "var(--accent)" }}>{initials}</div>
      ) : (
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
             style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
          <ChatCircleDots size={13} style={{ color: tone.color }} weight="fill" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-[13px] font-medium truncate" style={{ color: "var(--text-primary)" }}>
            <span style={{ color: tone.color }}>{tone.label}</span>
            <span style={{ color: "var(--text-tertiary)" }}> · </span>
            <span>{row.user_name || row.user_email}</span>
          </div>
          <div className="text-[11px] font-mono shrink-0" style={{ color: "var(--text-tertiary)" }}>{ago}</div>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <TypeIcon size={11} style={{ color: "var(--text-tertiary)" }} />
          <span className="text-[11.5px] font-mono truncate" style={{ color: "var(--text-secondary)" }}>{row.entity_id}</span>
        </div>
        {row.text && (
          <div className="text-[12.5px] mt-1 line-clamp-2" style={{ color: "var(--text-secondary)" }}>
            {row.text}
          </div>
        )}
      </div>
    </a>
  );
}

function relTime(date, t) {
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60)   return t("activity.justNow");
  if (diff < 3600) return `${Math.floor(diff / 60)}${t("activity.minAbbr")}`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}${t("activity.hAbbr")}`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}${t("activity.dAbbr")}`;
  return date.toLocaleDateString();
}
